/**
 * The device: an encrypted local store, the users enrolled on it, and their sessions.
 *
 *   const device = await Device.open();
 *   const session = await device.signInOnline(loginResponse, '480317');   // first time, online
 *   const session = await device.unlock(userId, '480317');                 // every other time, offline
 *   await session.save(patient);  await session.sync(transport);
 */
import { deepEqual, newId, patientIdOf, validateResource, } from '@assist/core';
import { DEFAULT_PBKDF2_ITERATIONS, deriveKek, generateDek, importEscrowKey, open, pinProblem, randomBytes, seal, unwrapKey, wrapKey, } from './crypto.js';
import { AssistDb } from './db.js';
import { NeedsOnlineLoginError, ValidationError, WeakPinError, WrongPinError } from './errors.js';
import { Mutex } from './mutex.js';
import { SyncEngine } from './sync.js';
export const aad = {
    cur: (key) => `rec:${key}:cur`,
    base: (key) => `rec:${key}:base`,
    op: (opId) => `op:${opId}`,
    dead: (opId) => `dl:${opId}`,
    session: (userId) => `meta:session:${userId}`,
};
export const recordKey = (type, id) => `${type}/${id}`;
export const stripMeta = (b) => {
    const { meta: _m, ...rest } = b;
    return rest;
};
export class Device {
    db;
    deviceId;
    options;
    mutex = new Mutex();
    wiped = false;
    constructor(db, deviceId, options) {
        this.db = db;
        this.deviceId = deviceId;
        this.options = options;
    }
    static async open(name = 'assist', options = {}) {
        const db = new AssistDb(name);
        await db.open();
        let id = (await db.meta.get('deviceId'))?.v;
        if (!id) {
            id = newId();
            await db.meta.put({ k: 'deviceId', v: id });
        }
        return new Device(db, id, {
            pbkdf2Iterations: options.pbkdf2Iterations ?? DEFAULT_PBKDF2_ITERATIONS,
            maxPinAttempts: options.maxPinAttempts ?? 5,
        });
    }
    async facilityId() {
        return (await this.db.meta.get('facilityId'))?.v ?? null;
    }
    async enrolledUsers() {
        return (await this.db.keyring.toArray()).map((k) => ({
            userId: k.userId,
            username: k.username,
            fullName: k.fullName,
        }));
    }
    /** After a successful online sign-in: open (or create) the store and enrol the user's PIN. */
    async signInOnline(login, pin) {
        this.assertOpen();
        const problem = pinProblem(pin);
        if (problem)
            throw new WeakPinError(problem);
        return this.mutex
            .run(async () => {
            const facility = await this.facilityId();
            if (facility && facility !== login.user.facilityId) {
                throw new Error('This device belongs to another facility');
            }
            const escrow = await importEscrowKey(login.deviceKey);
            const existing = (await this.db.meta.get('deviceWrap'))?.v;
            let dek;
            let keyId;
            let newDeviceWrap;
            if (existing) {
                try {
                    dek = await unwrapKey(existing, escrow);
                }
                catch {
                    throw new Error('The server key does not open this device store. Contact your administrator.');
                }
                keyId = (await this.db.meta.get('keyId'))?.v ?? 1;
            }
            else {
                dek = await generateDek();
                keyId = 1;
                newDeviceWrap = await wrapKey(dek, escrow);
            }
            const salt = randomBytes(16);
            const iterations = this.options.pbkdf2Iterations;
            const wrapped = await wrapKey(dek, await deriveKek(pin, salt, iterations));
            const auth = {
                token: login.token,
                expiresAt: login.expiresAt,
                user: login.user,
            };
            const sealedAuth = await seal(dek, auth, aad.session(login.user.id));
            await this.db.transaction('rw', [this.db.meta, this.db.keyring], async () => {
                await this.db.meta.put({ k: 'facilityId', v: login.user.facilityId });
                await this.db.meta.put({ k: 'keyId', v: keyId });
                if (newDeviceWrap)
                    await this.db.meta.put({ k: 'deviceWrap', v: newDeviceWrap });
                await this.db.meta.put({ k: `session:${login.user.id}`, v: sealedAuth });
                const row = {
                    userId: login.user.id,
                    username: login.user.username,
                    fullName: login.user.fullName,
                    salt,
                    iterations,
                    wrapped,
                    keyId,
                    failed: 0,
                };
                await this.db.keyring.put(row);
            });
            return new Session(this, login.user, new Map([[keyId, dek]]), keyId, auth);
        })
            .then(async (s) => {
            await s.resumeRotation();
            return s;
        });
    }
    /** Offline unlock with a PIN. */
    async unlock(userId, pin) {
        this.assertOpen();
        const s = await this.mutex.run(async () => {
            const entry = await this.db.keyring.get(userId);
            if (!entry)
                throw new NeedsOnlineLoginError();
            let dek;
            try {
                dek = await unwrapKey(entry.wrapped, await deriveKek(pin, entry.salt, entry.iterations));
            }
            catch {
                const failed = entry.failed + 1;
                if (failed >= this.options.maxPinAttempts) {
                    await this.db.transaction('rw', [this.db.keyring, this.db.meta], async () => {
                        await this.db.keyring.delete(userId);
                        await this.db.meta.delete(`session:${userId}`);
                    });
                    throw new NeedsOnlineLoginError('Too many wrong PINs. Sign in with your password (internet needed).');
                }
                await this.db.keyring.update(userId, { failed });
                throw new WrongPinError(this.options.maxPinAttempts - failed);
            }
            if (entry.failed)
                await this.db.keyring.update(userId, { failed: 0 });
            const keyId = (await this.db.meta.get('keyId'))?.v;
            if (entry.keyId !== keyId) {
                await this.db.keyring.delete(userId);
                throw new NeedsOnlineLoginError();
            }
            const sealedAuth = (await this.db.meta.get(`session:${userId}`))?.v;
            const auth = sealedAuth ? await open(dek, sealedAuth, aad.session(userId)) : null;
            const user = auth?.user ?? {
                id: userId,
                username: entry.username,
                fullName: entry.fullName,
                role: 'observer',
                facilityId: (await this.facilityId()) ?? '',
                facilityName: '',
            };
            return new Session(this, user, new Map([[keyId, dek]]), keyId, auth);
        });
        await s.resumeRotation();
        return s;
    }
    /** Erase everything on this device (revocation, or an administrator's reset). */
    async wipe() {
        this.wiped = true;
        await this.db.delete();
    }
    close() {
        this.db.close();
    }
    assertOpen() {
        if (this.wiped)
            throw new Error('This device store has been erased');
    }
}
/** An unlocked session for one user. Holds the data key in memory only. */
export class Session {
    device;
    user;
    keys;
    keyId;
    auth;
    locked = false;
    patientCache = null;
    constructor(device, user, 
    /** keyId -> data key. Two entries only while a key rotation is being finished. */
    keys, keyId, auth) {
        this.device = device;
        this.user = user;
        this.keys = keys;
        this.keyId = keyId;
        this.auth = auth;
    }
    get db() {
        return this.device.db;
    }
    key(keyId = this.keyId) {
        if (this.locked)
            throw new Error('Session is locked');
        const k = this.keys.get(keyId);
        if (!k)
            throw new Error(`No data key ${keyId} in this session`);
        return k;
    }
    openSealed(keyId, s, ad) {
        return open(this.key(keyId), s, ad);
    }
    sealNow(value, ad) {
        return seal(this.key(), value, ad);
    }
    /** Re-seal a value under the current key (no-op cost if already current). */
    async reseal(keyId, s, ad) {
        if (keyId === this.keyId)
            return s;
        return this.sealNow(await this.openSealed(keyId, s, ad), ad);
    }
    invalidateCaches() {
        this.patientCache = null;
    }
    // ---------------------------------------------------------------- records
    /** Validate and store a record locally, and queue it for the server. */
    async save(resource) {
        const v = validateResource(resource);
        if (!v.ok)
            throw new ValidationError(v.error);
        const r = v.resource;
        const key = recordKey(r.resourceType, r.id);
        return this.device.mutex.run(async () => {
            const rec = await this.db.records.get(key);
            const cur = rec ? await this.openSealed(rec.keyId, rec.cur, aad.cur(key)) : null;
            const next = { ...stripMeta(r), ...(cur?.meta ? { meta: cur.meta } : {}) };
            if (cur && deepEqual(stripMeta(cur), stripMeta(next)))
                return cur;
            const ops = await this.db.outbox.where('key').equals(key).sortBy('seq');
            const last = ops[ops.length - 1];
            let opRow;
            if (last && last.state === 'queued') {
                // Never sent: fold this edit into it (same base, newest content).
                const p = await this.openSealed(last.keyId, last.payload, aad.op(last.opId));
                opRow = {
                    ...last,
                    keyId: this.keyId,
                    payload: await this.sealNow({ resource: next, baseBody: p.baseBody }, aad.op(last.opId)),
                };
            }
            else {
                const opId = newId();
                opRow = {
                    opId,
                    seq: 0, // assigned in the transaction
                    key,
                    state: 'queued',
                    // An edit made while an earlier one is in flight waits for its outcome (dependsOn).
                    baseVersionId: last ? null : (rec?.serverVersion ?? null),
                    ...(last ? { dependsOn: last.opId } : {}),
                    attempts: 0,
                    deferrals: 0,
                    keyId: this.keyId,
                    payload: await this.sealNow({ resource: next, baseBody: cur }, aad.op(opId)),
                };
            }
            const curSealed = await this.sealNow(next, aad.cur(key));
            const baseSealed = rec?.base ? await this.reseal(rec.keyId, rec.base, aad.base(key)) : null;
            const isNewOp = !(last && last.state === 'queued');
            await this.db.transaction('rw', [this.db.records, this.db.outbox, this.db.meta], async () => {
                if (isNewOp) {
                    const seq = ((await this.db.meta.get('opSeq'))?.v ?? 0) + 1;
                    await this.db.meta.put({ k: 'opSeq', v: seq });
                    opRow.seq = seq;
                }
                await this.db.outbox.put(opRow);
                const row = {
                    key,
                    type: r.resourceType,
                    id: r.id,
                    patientId: patientIdOf(r),
                    serverVersion: rec?.serverVersion ?? null,
                    pending: 1,
                    updatedAt: Date.now(),
                    keyId: this.keyId,
                    cur: curSealed,
                    base: baseSealed,
                };
                await this.db.records.put(row);
            });
            this.invalidateCaches();
            return next;
        });
    }
    async get(type, id) {
        const key = recordKey(type, id);
        const rec = await this.db.records.get(key);
        return rec ? this.openSealed(rec.keyId, rec.cur, aad.cur(key)) : null;
    }
    async list(type) {
        const rows = await this.db.records.where('type').equals(type).toArray();
        return Promise.all(rows.map((r) => this.openSealed(r.keyId, r.cur, aad.cur(r.key))));
    }
    async forPatient(patientId) {
        const rows = await this.db.records.where('patientId').equals(patientId).toArray();
        return Promise.all(rows.map((r) => this.openSealed(r.keyId, r.cur, aad.cur(r.key))));
    }
    /** Search patients by any part of the name, MRN or phone. Decrypts in memory only. */
    async searchPatients(query, limit = 50) {
        this.patientCache ??= await this.list('Patient');
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.length)
            return this.patientCache.slice(0, limit);
        return this.patientCache
            .filter((p) => {
            const hay = [
                ...(p.name ?? []).flatMap((n) => [n.text ?? '', ...(n.given ?? []), n.family ?? '']),
                ...(p.identifier ?? []).map((i) => i.value),
                ...(p.telecom ?? []).map((t) => t.value),
            ]
                .join(' ')
                .toLowerCase();
            return terms.every((t) => hay.includes(t));
        })
            .slice(0, limit);
    }
    async status() {
        return {
            pendingRecords: await this.db.records.where('pending').equals(1).count(),
            outbox: await this.db.outbox.count(),
            failed: await this.db.deadletter.count(),
            lastSyncAt: (await this.db.meta.get('lastSyncAt'))?.v ?? null,
        };
    }
    async failedEntries() {
        const rows = await this.db.deadletter.toArray();
        return Promise.all(rows.map(async (d) => ({
            opId: d.opId,
            key: d.key,
            at: d.at,
            error: d.error,
            resource: (await this.openSealed(d.keyId, d.payload, aad.dead(d.opId)))
                .resource,
        })));
    }
    // ---------------------------------------------------------------- sync
    sync(transport) {
        return new SyncEngine(this).run(transport);
    }
    // ---------------------------------------------------------------- keys
    /**
     * Replace the data key. Needs the user's PIN and a fresh device key from an online sign-in.
     * Other users' PINs stop working (they sign in online once). Re-encryption continues after a
     * crash: the old key is kept, wrapped under the new one, until every row is re-sealed.
     */
    async rotateKey(pin, deviceKeyB64) {
        await this.device.mutex.run(async () => {
            const entry = await this.db.keyring.get(this.user.id);
            if (!entry)
                throw new NeedsOnlineLoginError();
            try {
                await unwrapKey(entry.wrapped, await deriveKek(pin, entry.salt, entry.iterations));
            }
            catch {
                throw new WrongPinError(this.device.options.maxPinAttempts - entry.failed);
            }
            const oldKeyId = this.keyId;
            const oldDek = this.key(oldKeyId);
            const newDek = await generateDek();
            const newKeyId = oldKeyId + 1;
            const deviceWrap = await wrapKey(newDek, await importEscrowKey(deviceKeyB64));
            const salt = randomBytes(16);
            const iterations = this.device.options.pbkdf2Iterations;
            const wrapped = await wrapKey(newDek, await deriveKek(pin, salt, iterations));
            const prevWrap = await wrapKey(oldDek, newDek);
            const sealedAuth = this.auth
                ? await seal(newDek, this.auth, aad.session(this.user.id))
                : null;
            await this.db.transaction('rw', [this.db.meta, this.db.keyring], async () => {
                await this.db.meta.put({ k: 'keyId', v: newKeyId });
                await this.db.meta.put({ k: 'deviceWrap', v: deviceWrap });
                await this.db.meta.put({ k: 'prevKey', v: { keyId: oldKeyId, wrapped: prevWrap } });
                const sessionKeys = (await this.db.meta.toArray())
                    .filter((m) => m.k.startsWith('session:'))
                    .map((m) => m.k);
                await this.db.meta.bulkDelete(sessionKeys);
                if (sealedAuth)
                    await this.db.meta.put({ k: `session:${this.user.id}`, v: sealedAuth });
                await this.db.keyring.clear();
                await this.db.keyring.put({
                    ...entry,
                    salt,
                    iterations,
                    wrapped,
                    keyId: newKeyId,
                    failed: 0,
                });
            });
            this.keys.set(newKeyId, newDek);
            this.keyId = newKeyId;
        });
        await this.reencryptAll();
    }
    /** Finish an interrupted key rotation (called on every unlock). */
    async resumeRotation() {
        const prev = (await this.db.meta.get('prevKey'))?.v;
        if (!prev)
            return;
        if (!this.keys.has(prev.keyId))
            this.keys.set(prev.keyId, await unwrapKey(prev.wrapped, this.key()));
        await this.reencryptAll();
    }
    /** Re-seal every row still under an older key, in small batches. */
    async reencryptAll(batch = 200) {
        const db = this.db;
        for (;;) {
            const done = await this.device.mutex.run(async () => {
                const recs = await db.records.where('keyId').below(this.keyId).limit(batch).toArray();
                const ops = await db.outbox.where('keyId').below(this.keyId).limit(batch).toArray();
                const dead = await db.deadletter.where('keyId').below(this.keyId).limit(batch).toArray();
                if (!recs.length && !ops.length && !dead.length)
                    return true;
                const recs2 = await Promise.all(recs.map(async (r) => ({
                    ...r,
                    keyId: this.keyId,
                    cur: await this.reseal(r.keyId, r.cur, aad.cur(r.key)),
                    base: r.base ? await this.reseal(r.keyId, r.base, aad.base(r.key)) : null,
                })));
                const ops2 = await Promise.all(ops.map(async (o) => ({
                    ...o,
                    keyId: this.keyId,
                    payload: await this.reseal(o.keyId, o.payload, aad.op(o.opId)),
                })));
                const dead2 = await Promise.all(dead.map(async (d) => ({
                    ...d,
                    keyId: this.keyId,
                    payload: await this.reseal(d.keyId, d.payload, aad.dead(d.opId)),
                })));
                await db.transaction('rw', [db.records, db.outbox, db.deadletter], async () => {
                    await db.records.bulkPut(recs2);
                    await db.outbox.bulkPut(ops2);
                    await db.deadletter.bulkPut(dead2);
                });
                return false;
            });
            if (done)
                break;
        }
        await this.device.mutex.run(async () => {
            await db.meta.delete('prevKey');
            for (const k of [...this.keys.keys()])
                if (k !== this.keyId)
                    this.keys.delete(k);
        });
    }
    /** Forget the data key (screen lock / user switch). */
    lock() {
        this.locked = true;
        this.keys.clear();
        this.patientCache = null;
    }
}
//# sourceMappingURL=device.js.map